import { useEffect, useRef, useState } from 'react'
import {
  COPY_SLOTS,
  api,
  type Campaign,
  type CopySlot,
  type Cycle,
  type Filter,
  type Job,
  type StaleReport,
  type StyleInfo,
} from '../api'
import { useRefreshOnActivate } from '../useActive'
import { useNotice } from '../useNotice'
import { CycleView } from './Cycle'

/**
 * Campaigns — home.
 *
 * A campaign is the standing program (D83): filter, copy, prompt, style,
 * schedule, cooldown, review and dispatch policy. This screen is its list, its
 * editor, and the three acts only a person performs here:
 *
 *   Approve copy   a person read the six slots and the prompt (D88). Required
 *                  to enable. Any edit to what the campaign says clears it.
 *   Enable         the loop switches on; the scheduler ticks it on schedule.
 *   Arm            a person types the slug to let the scheduler SEND (D89).
 *                  Requires enabled, approved copy, and a rehearsal — a
 *                  redirected test send, not a dry run (D107).
 *
 * None of those three has an MCP tool, on purpose.
 */
export function CampaignsTab({
  active = true,
  campaignId,
  cycleId,
  creating: creatingProp,
  onOpenCampaign,
  onOpenCycle,
  onNewCampaign,
  onCloseCampaign,
  onCloseCycle,
  onCancelNew,
  onCreated,
  onDeleted,
  onGoToFilters,
}: {
  active?: boolean
  campaignId?: number | null
  cycleId?: number | null
  creating?: boolean
  onOpenCampaign?: (id: number) => void
  onOpenCycle?: (cycleId: number, campaignId: number | null) => void
  onNewCampaign?: () => void
  onCloseCampaign?: () => void
  onCloseCycle?: (campaignId: number | null) => void
  onCancelNew?: () => void
  onCreated?: (id: number) => void
  onDeleted?: () => void
  onGoToFilters?: () => void
}) {
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null)
  const [stuck, setStuck] = useState<StaleReport | null>(null)
  const [selected, setSelected] = useState<number | null>(campaignId ?? null)
  const [openCycle, setOpenCycle] = useState<number | null>(cycleId ?? null)
  const [creating, setCreating] = useState(creatingProp ?? false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [view, setView] = useState<ListView>('all')

  useEffect(() => {
    if (!active) return
    setSelected(campaignId ?? null)
    setOpenCycle(cycleId ?? null)
    setCreating(creatingProp ?? false)
  }, [active, campaignId, cycleId, creatingProp])

  const load = async () => {
    try {
      setError(null)
      const [list, stale] = await Promise.all([api.campaigns(), api.staleCycles()])
      setCampaigns(list)
      setStuck(stale)
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  useRefreshOnActivate(active, load)

  if (error) return <div className="error">{error}</div>
  if (!campaigns) return <div className="loading">Loading campaigns…</div>

  if (openCycle != null) {
    return (
      <CycleView
        cycleId={openCycle}
        onBack={() => {
          if (onCloseCycle) onCloseCycle(selected)
          else setOpenCycle(null)
          void load()
        }}
      />
    )
  }

  if (selected != null) {
    return (
      <CampaignEditor
        campaignId={selected}
        onBack={() => {
          if (onCloseCampaign) onCloseCampaign()
          else setSelected(null)
          void load()
        }}
        onOpenCycle={(id) => {
          if (onOpenCycle) onOpenCycle(id, selected)
          else setOpenCycle(id)
        }}
        onDeleted={onDeleted}
      />
    )
  }

  const openCampaign = (id: number) => {
    if (onOpenCampaign) onOpenCampaign(id)
    else setSelected(id)
  }
  const openCycleFromList = (id: number) => {
    if (onOpenCycle) onOpenCycle(id, null)
    else setOpenCycle(id)
  }
  const needs = campaigns.filter(needsYou)
  const views: { id: ListView; label: string; rows: Campaign[] }[] = [
    { id: 'all', label: 'All', rows: campaigns },
    { id: 'needs', label: 'Needs you', rows: needs },
    { id: 'on', label: 'On', rows: campaigns.filter((c) => c.enabled) },
    { id: 'off', label: 'Off', rows: campaigns.filter((c) => !c.enabled) },
  ]
  const visible = fuzzyFilterCampaigns(views.find((v) => v.id === view)!.rows, search)

  return (
    <>
      <div className="page-head">
        <h1>Campaigns</h1>
        <span className="sub">Standing programs: who to reach, what to say, how often to look.</span>
      </div>
      <div className="with-rail">
        <div>
          {creating && (
            <NewCampaign
              onCancel={() => {
                if (onCancelNew) onCancelNew()
                else setCreating(false)
              }}
              onCreated={(id) => {
                void load()
                if (onCreated) onCreated(id)
                else {
                  setCreating(false)
                  setSelected(id)
                }
              }}
              onGoToFilters={onGoToFilters}
            />
          )}
          <div className="panel">
            <div className="panel-body compact">
              <div className="row">
                <div className="chips" role="group" aria-label="Show">
                  {views.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={`chip${view === v.id ? ' on' : ''}`}
                      aria-pressed={view === v.id}
                      onClick={() => setView(v.id)}
                    >
                      {v.label} · {v.rows.length}
                    </button>
                  ))}
                </div>
                <input
                  type="search"
                  aria-label="Search campaigns"
                  value={search}
                  placeholder="Search name, slug, filter, state…"
                  spellCheck={false}
                  autoComplete="off"
                  style={{ maxWidth: 300, marginLeft: 'auto' }}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            {campaigns.length === 0 ? (
              <div className="empty">No campaigns yet.</div>
            ) : visible.length === 0 ? (
              <div className="empty">
                {search.trim() ? <>No campaigns match <span className="mono">{search.trim()}</span>.</> : 'Nothing here.'}
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th className="num">#</th>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th className="num" title="Drafts in open cycles waiting for a person">Waiting</th>
                    <th className="num">Sent</th>
                    <th>Last cycle</th>
                    <th>Next look</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((c) => (
                    <tr key={c.id} className="clickable" onClick={() => openCampaign(c.id)}>
                      <td className="num mono">{c.id}</td>
                      <td>
                        <a
                          href={`/campaigns/${c.id}`}
                          style={{ color: 'var(--text)', textDecoration: 'none' }}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            openCampaign(c.id)
                          }}
                        >
                          {c.name}
                        </a>
                        <div className="dim" style={{ fontSize: 11.5 }}>
                          {c.filter_title && c.filter_title !== c.name ? c.filter_title : <span className="mono">{c.filter}</span>}
                          {c.filter_parked && <> <span className="badge parked">filter parked</span></>}
                        </div>
                      </td>
                      <td><StatusBadges c={c} /></td>
                      <td className="num">
                        {(c.open_awaiting_review ?? 0) > 0 ? (
                          <strong style={{ color: 'var(--warn)' }}>{c.open_awaiting_review}</strong>
                        ) : (
                          <span className="dim">—</span>
                        )}
                        {(c.open_approved ?? 0) > 0 && (
                          <div className="dim" style={{ fontSize: 11.5 }}>+{c.open_approved} approved</div>
                        )}
                      </td>
                      <td className="num">{c.sent_total ? c.sent_total.toLocaleString() : <span className="dim">—</span>}</td>
                      <td>
                        {c.last_cycle_id != null ? (
                          <span className="mono">
                            #{c.last_cycle_id} <CycleBadge status={c.last_cycle_status ?? ''} />
                          </span>
                        ) : (
                          <span className="dim">never run</span>
                        )}
                        {(c.stale_cycles ?? 0) > 0 && (
                          <>
                            {' '}
                            <span className="badge bad" title={`${c.stale_holding ?? 0} account(s) held out of every later cycle`}>
                              {c.stale_cycles} stuck
                            </span>
                          </>
                        )}
                      </td>
                      <td>{c.enabled ? when(c.next_run_at) : <span className="dim">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <aside className="rail" aria-label="What needs you">
          {!creating && (
            <button
              className="primary block"
              onClick={() => {
                if (onNewCampaign) onNewCampaign()
                else setCreating(true)
              }}
            >
              + New campaign
            </button>
          )}
          <NeedsYouCard
            campaigns={needs}
            stuck={stuck}
            onOpenCampaign={openCampaign}
            onOpenCycle={openCycleFromList}
            onChanged={load}
          />
          <GlanceCard campaigns={campaigns} onOpenCampaign={openCampaign} />
        </aside>
      </div>
    </>
  )
}

type ListView = 'all' | 'needs' | 'on' | 'off'

/** Something on this campaign is waiting on a person, or is wrong while it runs. */
function needsYou(c: Campaign): boolean {
  return (
    (c.open_awaiting_review ?? 0) > 0 ||
    (c.open_approved ?? 0) > 0 ||
    (c.stale_cycles ?? 0) > 0 ||
    (c.enabled && !c.copy_approved)
  )
}

function when(iso: string | null): string {
  if (!iso) return 'now'
  const d = new Date(iso)
  const ms = d.getTime() - Date.now()
  if (ms <= 0) return 'due'
  const h = Math.round(ms / 3_600_000)
  return h < 1 ? `in ${Math.max(1, Math.round(ms / 60_000))}m` : h < 48 ? `in ${h}h` : d.toLocaleString()
}

/** Haystack for list search — name, ids, filter, and plain-language state words. */
function campaignSearchText(c: Campaign): string {
  return [
    String(c.id),
    c.name,
    c.slug,
    c.filter,
    c.filter_title ?? '',
    c.enabled ? 'enabled on loop on' : 'disabled off',
    c.copy_approved ? 'copy approved' : 'copy unapproved needs approval',
    c.review_mode,
    c.dispatch_mode,
    c.armed ? 'armed' : 'not armed',
    (c.stale_cycles ?? 0) > 0 ? 'stuck' : '',
    (c.awaiting_review ?? 0) > 0 ? 'awaiting review drafts waiting' : '',
  ].join(' ')
}

/**
 * Fuzzy filter: each whitespace-separated token must match as a subsequence
 * (or as a contiguous substring). Better scores float to the top. No dependency.
 */
function fuzzyScore(haystack: string, needle: string): number {
  const h = haystack.toLowerCase()
  const n = needle.toLowerCase()
  if (!n) return 0
  const sub = h.indexOf(n)
  if (sub >= 0) return 1000 - Math.min(sub, 200) + Math.min(n.length, 50)
  let hi = 0
  let score = 0
  let streak = 0
  for (let i = 0; i < n.length; i++) {
    const ch = n[i]!
    const found = h.indexOf(ch, hi)
    if (found < 0) return -1
    streak = found === hi ? streak + 1 : 1
    score += 1 + streak * 2
    if (found === 0 || /[\s_\-/]/.test(h[found - 1] ?? '')) score += 8
    hi = found + 1
  }
  return score
}

function fuzzyFilterCampaigns(list: Campaign[], query: string): Campaign[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return list
  return list
    .map((c) => {
      const text = campaignSearchText(c)
      let total = 0
      for (const t of tokens) {
        const s = fuzzyScore(text, t)
        if (s < 0) return null
        total += s
      }
      return { c, total }
    })
    .filter((x): x is { c: Campaign; total: number } => x != null)
    .sort((a, b) => b.total - a.total || a.c.name.localeCompare(b.c.name))
    .map((x) => x.c)
}

type NextStep = {
  headline: string
  detail: string
  tone: 'act' | 'warn' | 'calm'
  action?: { label: string; cycleId?: number; jump?: string }
}

/** The one thing to do next on this campaign — the rail's lead card. */
function nextStepFor(c: Campaign): NextStep {
  const enableBlockers = c.enable_blockers ?? []
  const waiting = c.open_awaiting_review ?? 0
  const approved = c.open_approved ?? 0
  const open = (c.cycles ?? []).filter((cy) => cy.open)
  const reviewIn = open.filter((cy) => cy.awaiting_review > 0)
  const sendIn = open.filter((cy) => cy.approved > 0)
  if (!c.copy_approved) {
    return {
      headline: 'Approve the copy and AI instructions',
      detail: 'Read them on Setup, then press Approve copy below. Nothing can run until a person has.',
      tone: 'warn',
      action: { label: 'Read the copy', jump: 'sec-copy' },
    }
  }
  if (waiting > 0) {
    return {
      headline: `${waiting.toLocaleString()} draft${waiting === 1 ? '' : 's'} waiting for review`,
      detail:
        reviewIn.length > 1
          ? `Across ${reviewIn.length} open cycles. Nothing sends until a draft is approved.`
          : 'Nothing sends until a draft is approved.',
      tone: 'act',
      action: reviewIn[0] ? { label: `Review cycle ${reviewIn[0].id}`, cycleId: reviewIn[0].id } : undefined,
    }
  }
  if (approved > 0) {
    return {
      headline: `${approved.toLocaleString()} approved, not sent yet`,
      detail: 'Send them from the cycle, or cancel the cycle to let those people go.',
      tone: 'act',
      action: sendIn[0] ? { label: `Send cycle ${sendIn[0].id}`, cycleId: sendIn[0].id } : undefined,
    }
  }
  if (!c.enabled) {
    return enableBlockers.length
      ? {
          headline: 'Cannot switch on yet',
          detail: `${enableBlockers.join('; ')}. You can still run one cycle now to try it.`,
          tone: 'warn',
        }
      : {
          headline: 'Ready · campaign is off',
          detail: 'Turn it on to run on its schedule, or run one cycle now to draft without turning it on.',
          tone: 'calm',
        }
  }
  if (c.dispatch_mode === 'auto' && !c.armed) {
    const armBlockers = c.arm_blockers ?? []
    return {
      headline: 'On · automatic sending not armed',
      detail: armBlockers.length
        ? `Cannot arm yet: ${armBlockers.join('; ')}.`
        : 'Drafts stop at review until a person arms it under Sending.',
      tone: 'calm',
    }
  }
  return {
    headline: `On · next look ${when(c.next_run_at)}`,
    detail: 'Nothing is waiting on you.',
    tone: 'calm',
  }
}

function StatusBadges({ c }: { c: Campaign }) {
  return (
    <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
      {c.enabled ? <span className="badge ok">On</span> : <span className="badge catchall">Off</span>}
      {!c.copy_approved && <span className="badge parked">copy not approved</span>}
      {c.message_class === 'service' && <span className="badge service">service</span>}
      {c.dispatch_mode === 'auto' &&
        (c.armed ? (
          <span className="badge bad">sends automatically</span>
        ) : (
          <span className="badge catchall">auto-send not armed</span>
        ))}
    </span>
  )
}

/**
 * Everything on the list that is waiting on a person, in one card.
 *
 * Stuck cycles come first (D106): an account with an unsent draft in an open
 * cycle is skipped by every later tick of that campaign (D86), so a cycle
 * nobody finished quietly takes its people out of the campaign. Cancel is
 * offered here because it sends nothing — except on a cycle stuck in
 * `dispatching`, where some people may already have been mailed and the
 * sendlog has to be read first.
 */
function NeedsYouCard({
  campaigns,
  stuck,
  onOpenCampaign,
  onOpenCycle,
  onChanged,
}: {
  campaigns: Campaign[]
  stuck: StaleReport | null
  onOpenCampaign: (id: number) => void
  onOpenCycle: (id: number) => void
  onChanged: () => Promise<void>
}) {
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const cancel = async (id: number) => {
    setBusy(id)
    setError(null)
    try {
      await api.cancelCycle(id)
      await onChanged()
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(null)
    }
  }
  const stuckCycles = stuck?.cycles ?? []
  const items: { key: string; title: string; meta: string; open: () => void }[] = []
  // One line per campaign, so this count and the "Needs you" chip agree.
  // Stuck cycles are listed above and counted there, not again here.
  for (const c of campaigns) {
    const waiting = c.open_awaiting_review ?? 0
    const approved = c.open_approved ?? 0
    const why = [
      waiting > 0 && `${waiting.toLocaleString()} waiting for review`,
      approved > 0 && `${approved.toLocaleString()} approved, not sent`,
      c.enabled && !c.copy_approved && 'on, but copy is not approved',
    ].filter(Boolean)
    if (why.length) items.push({ key: `c${c.id}`, title: c.name, meta: why.join(' · '), open: () => onOpenCampaign(c.id) })
  }
  const total = stuckCycles.length + items.length
  return (
    <div className={`panel lead${stuckCycles.length ? ' warn' : ''}`}>
      <h2>Needs you <span className="count">{total}</span></h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        {total === 0 ? (
          <p className="hint">Nothing is waiting on you.</p>
        ) : (
          <div className="rail-list">
            {stuckCycles.map((cy) => (
              <div
                key={`s${cy.id}`}
                className="rail-item clickable"
                role="button"
                tabIndex={0}
                title={[cy.stale_reason, cy.what_to_do].filter(Boolean).join(' — ')}
                onClick={() => onOpenCycle(cy.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') onOpenCycle(cy.id) }}
              >
                <div className="row" style={{ justifyContent: 'space-between', gap: 6 }}>
                  <span>
                    <span className="badge bad">stuck</span> Cycle {cy.id}
                  </span>
                  {cy.status !== 'dispatching' && (
                    <button
                      disabled={busy === cy.id}
                      style={{ padding: '1px 8px', fontSize: 12 }}
                      onClick={(e) => { e.stopPropagation(); void cancel(cy.id) }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <div className="rail-item-meta">
                  {cy.campaign_name} · {cy.status.replace('_', ' ')} for {age(cy.age_hours)}
                  {cy.holding > 0 ? ` · holding ${cy.holding}` : ''}
                </div>
              </div>
            ))}
            {items.map((it) => (
              <button key={it.key} className="rail-item" onClick={it.open}>
                <div>{it.title}</div>
                <div className="rail-item-meta">{it.meta}</div>
              </button>
            ))}
          </div>
        )}
        {stuckCycles.length > 0 && stuck && (
          <p className="hint caption">
            A stuck cycle keeps its people out of every later cycle of its campaign until it closes.
            Stuck means {stuck.thresholds.stale_after_days} days waiting for a person, or{' '}
            {stuck.thresholds.abandoned_after_hours} hours in a status only a running worker holds.
          </p>
        )}
      </div>
    </div>
  )
}

function GlanceCard({ campaigns, onOpenCampaign }: { campaigns: Campaign[]; onOpenCampaign: (id: number) => void }) {
  const on = campaigns.filter((c) => c.enabled)
  const sent = campaigns.reduce((n, c) => n + (c.sent_total ?? 0), 0)
  const next = on
    .filter((c) => c.next_run_at)
    .sort((a, b) => a.next_run_at!.localeCompare(b.next_run_at!))[0]
  return (
    <div className="panel">
      <h2>At a glance</h2>
      <div className="panel-body">
        <div className="stat-row">
          <div><div className="stat">{on.length}</div><div className="stat-label">on</div></div>
          <div><div className="stat">{campaigns.length - on.length}</div><div className="stat-label">off</div></div>
          <div><div className="stat">{sent.toLocaleString()}</div><div className="stat-label">sent, all time</div></div>
        </div>
        {next && (
          <p className="hint">
            Next look {when(next.next_run_at)}:{' '}
            <button className="linkish" onClick={() => onOpenCampaign(next.id)}>{next.name}</button>
          </p>
        )}
      </div>
    </div>
  )
}

export function StaleBadge({ kind }: { kind: 'abandoned' | 'waiting' | null }) {
  if (!kind) return null
  return (
    <span
      className={`badge ${kind === 'abandoned' ? 'bad' : 'parked'}`}
      title={
        kind === 'abandoned'
          ? 'no worker is left to finish this — it will not move on its own'
          : 'open and waiting for a person for longer than the threshold'
      }
    >
      {kind}
    </span>
  )
}

export function age(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`
  if (hours < 48) return `${Math.round(hours)}h`
  return `${Math.round(hours / 24)}d`
}

export function CycleBadge({ status }: { status: string }) {
  const cls =
    status === 'sent' ? 'ok'
    : status === 'failed' ? 'bad'
    : status === 'cancelled' ? 'catchall'
    : status === 'approved' ? 'ok'
    : status === 'awaiting_review' ? 'parked'
    : 'running'
  return <span className={`badge ${cls}`}>{status.replace('_', ' ')}</span>
}

// ---------------------------------------------------------------------------
// New campaign
// ---------------------------------------------------------------------------

function NewCampaign({
  onCreated,
  onCancel,
  onGoToFilters,
}: {
  onCreated: (id: number) => void
  onCancel: () => void
  onGoToFilters?: () => void
}) {
  const [filters, setFilters] = useState<Filter[] | null>(null)
  const [filter, setFilter] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const all = await api.filters()
        setFilters(all)
        const usable = all.filter((f) => !f.parked && !f.unconditional)
        setFilter((cur) => cur || usable[0]?.name || '')
      } catch (err) {
        setError(String((err as Error).message))
      }
    })()
  }, [])

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      const c = await api.createCampaign({
        name: name.trim(),
        filter,
        description: description.trim() || undefined,
      })
      onCreated(c.id)
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(false)
    }
  }

  const usable = (filters ?? []).filter((f) => !f.parked && !f.unconditional)

  return (
    <div className="panel">
      <h2>New campaign</h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        <p className="hint">
          Created disabled, with human review and manual dispatch. Copy slots start empty and fall
          through to the approved fragment library; the prompt starts from the shared base with a
          section for this audience to fill in. A person approves the copy before it can be
          enabled.
        </p>
        {filters && usable.length === 0 ? (
          <div className="notice">
            No filter can back a campaign yet — every filter is parked or has no condition.{' '}
            {onGoToFilters && (
              <button className="linkish" onClick={onGoToFilters}>Define one on Filters →</button>
            )}
          </div>
        ) : (
          <div className="row-form">
            <div style={{ minWidth: 320 }}>
              <label htmlFor="nc-filter">Audience (filter)</label>
              <select
                id="nc-filter"
                value={filter}
                disabled={!filters}
                style={{ width: '100%' }}
                onChange={(e) => setFilter(e.target.value)}
              >
                {usable.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.title} — {f.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: 260 }}>
              <label htmlFor="nc-name">Name</label>
              <input id="nc-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div style={{ minWidth: 320, flex: 1 }}>
              <label htmlFor="nc-desc">What an email from it is for (optional)</label>
              <input id="nc-desc" type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <button className="primary" disabled={busy || !name.trim() || !filter} onClick={() => void create()}>
                {busy ? 'Creating…' : 'Create'}
              </button>
              <button disabled={busy} onClick={onCancel} style={{ marginLeft: 6 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

type EditorTab = 'overview' | 'setup' | 'more'

const SETUP_SECTIONS: { id: string; label: string }[] = [
  { id: 'sec-audience', label: 'Audience' },
  { id: 'sec-copy', label: 'Copy' },
  { id: 'sec-prompt', label: 'AI instructions' },
  { id: 'sec-look', label: 'Look' },
  { id: 'sec-schedule', label: 'Schedule & policy' },
]

function CampaignEditor({
  campaignId,
  onBack,
  onOpenCycle,
  onDeleted,
}: {
  campaignId: number
  onBack: () => void
  onOpenCycle: (cycleId: number) => void
  onDeleted?: () => void
}) {
  const [c, setC] = useState<Campaign | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useNotice(8000)
  const [tab, setTab] = useState<EditorTab>('overview')
  // A jump asked for from another tab has to wait for that tab to render.
  const [jump, setJump] = useState<string | null>(null)

  const load = async () => {
    try {
      setError(null)
      setC(await api.campaign(campaignId))
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    void load()
  }, [campaignId])

  useEffect(() => {
    if (!jump) return
    document.getElementById(jump)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setJump(null)
  }, [jump, tab])

  const goTo = (section: string) => {
    setTab('setup')
    setJump(section)
  }

  const save = async (body: Record<string, unknown>, what: string) => {
    try {
      const out = await api.updateCampaign(campaignId, body)
      let msg = `${what} saved`
      if (out.copy_approval_withdrawn) msg += ' — copy approval withdrawn; a person must re-approve'
      if (out.disarmed) msg += ' — and the campaign is DISARMED'
      setNotice(msg)
      await load()
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  if (error && !c) return <div className="error">{error}</div>
  if (!c) return <div className="loading">Loading…</div>

  return (
    <>
      <div className="page-head">
        <button className="linkish crumb" onClick={onBack}>← Campaigns</button>
        <h1>{c.name}</h1>
        <StatusBadges c={c} />
        <span className="sub mono">#{c.id} · {c.slug}</span>
      </div>
      {error && <div className="error">{error}</div>}
      {notice && <div className="notice">{notice}</div>}

      <div className="with-rail">
        <div>
          <nav className="subtabs">
            <button aria-current={tab === 'overview'} onClick={() => setTab('overview')}>Overview</button>
            <button aria-current={tab === 'setup'} onClick={() => setTab('setup')}>Setup</button>
            <button aria-current={tab === 'more'} onClick={() => setTab('more')}>
              Never mail &amp; delete{(c.exclusions?.length ?? 0) > 0 ? ` (${c.exclusions!.length})` : ''}
            </button>
          </nav>

          {tab === 'overview' && (
            <>
              <SummaryPanel c={c} onEdit={goTo} />
              <CyclesPanel c={c} onOpenCycle={onOpenCycle} onChanged={load} />
            </>
          )}
          {tab === 'setup' && (
            <>
              <AudiencePanel c={c} onSave={save} />
              <CopyPanel c={c} onSave={save} />
              <PromptPanel c={c} onSave={save} />
              <StylePanel c={c} onSave={save} />
              <SchedulePanel c={c} onSave={save} />
            </>
          )}
          {tab === 'more' && (
            <>
              <ExclusionsPanel c={c} onChanged={load} />
              <DangerPanel c={c} onDeleted={onDeleted ?? onBack} />
            </>
          )}
        </div>

        <CampaignRail
          c={c}
          tab={tab}
          onOpenCycle={onOpenCycle}
          onJump={goTo}
          onChanged={load}
          onNotice={setNotice}
          onError={setError}
        />
      </div>
    </>
  )
}

/** The whole campaign in plain words, each line one click from its editor. */
function SummaryPanel({ c, onEdit }: { c: Campaign; onEdit: (section: string) => void }) {
  const slots = COPY_SLOTS.filter((s) => (c.copy[s] ?? '').trim()).length
  const edit = (section: string) => (
    <button className="linkish" style={{ marginLeft: 8 }} onClick={() => onEdit(section)}>Edit</button>
  )
  return (
    <div className="panel">
      <h2>What this campaign does</h2>
      <div className="panel-body">
        {c.description && <p className="hint">{c.description}</p>}
        <dl className="facts">
          <dt>Who</dt>
          <dd>
            {c.filter_title || c.filter} <span className="mono dim">({c.filter})</span>
            {c.filter_parked && <> <span className="badge parked">parked</span></>}
            {edit('sec-audience')}
            <div className="mono dim" style={{ fontSize: 11.5, marginTop: 2 }}>
              {c.filter_when ?? 'no condition — matches everyone'}
            </div>
          </dd>
          <dt>What</dt>
          <dd>
            {slots} of 6 copy slots set{slots < 6 ? ', the rest from the library' : ''} · AI instructions{' '}
            {(c.system_prompt?.length ?? c.system_prompt_chars).toLocaleString()} characters
            {edit('sec-copy')}
          </dd>
          <dt>Look</dt>
          <dd>
            <span className="mono">{c.style ?? 'default'}</span> style ·{' '}
            {c.message_class === 'service' ? 'service email, no unsubscribe footer' : 'campaign email, with unsubscribe footer'}
            {edit('sec-look')}
          </dd>
          <dt>When</dt>
          <dd>
            Looks every {c.interval_hours}h{c.enabled ? `, next ${when(c.next_run_at)}` : ' when on'} · waits{' '}
            {c.cooldown_days != null ? `${c.cooldown_days} days` : 'the default gap'} before writing to someone again ·{' '}
            {c.max_sends != null ? `at most ${c.max_sends} send${c.max_sends === 1 ? '' : 's'} per person` : 'no lifetime cap'}
            {edit('sec-schedule')}
          </dd>
          <dt>Review</dt>
          <dd>
            {c.review_mode === 'human'
              ? 'A person approves each draft'
              : 'Drafts that pass the checks are approved automatically'}
          </dd>
          <dt>Sending</dt>
          <dd>
            {c.dispatch_mode === 'manual'
              ? 'A person sends each cycle'
              : c.armed
                ? 'The scheduler sends approved drafts on its own (armed)'
                : 'Set to send automatically, but not armed — nothing sends'}
          </dd>
        </dl>
      </div>
    </div>
  )
}

/**
 * The right column: next step, state, and the acts only a person performs
 * here — approve copy, switch on, run now, agent release, the send-gap
 * waiver, and arming. Same calls as before; only where they sit changed.
 */
function CampaignRail({
  c,
  tab,
  onOpenCycle,
  onJump,
  onChanged,
  onNotice,
  onError,
}: {
  c: Campaign
  tab: EditorTab
  onOpenCycle: (id: number) => void
  onJump: (section: string) => void
  onChanged: () => Promise<void>
  onNotice: (m: string) => void
  onError: (m: string | null) => void
}) {
  const [who, setWho] = useState(() => {
    try { return window.localStorage.getItem('reachout.operator') ?? '' } catch { return '' }
  })
  const [typedSlug, setTypedSlug] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [job, setJob] = useState<Job | null>(null)
  const timer = useRef<number | null>(null)

  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current) }, [])
  useEffect(() => {
    try { window.localStorage.setItem('reachout.operator', who) } catch { /* convenience */ }
  }, [who])

  const act = async (what: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(what)
    onError(null)
    try {
      await fn()
      onNotice(done)
      await onChanged()
    } catch (err) {
      onError(String((err as Error).message))
    } finally {
      setBusy(null)
    }
  }

  const runNow = async () => {
    setBusy('run')
    onError(null)
    setJob(null)
    try {
      const started = await api.runCampaign(c.id, { refine: true })
      const poll = async () => {
        const j = await api.job(started.job_id)
        setJob(j)
        if (j.status !== 'running') {
          if (timer.current) window.clearInterval(timer.current)
          timer.current = null
          setBusy(null)
          await onChanged()
        }
      }
      await poll()
      timer.current = window.setInterval(() => void poll(), 3000)
    } catch (err) {
      onError(String((err as Error).message))
      setBusy(null)
    }
  }

  const next = nextStepFor(c)
  const enableBlockers = c.enable_blockers ?? []
  const armBlockers = c.arm_blockers ?? []
  const noName = !who.trim()
  const outcome = job?.status !== 'running' && job
    ? ((job.detail as { outcomes?: { status: string; matched: number; drafted: number; skipped: Record<string, number>; error?: string }[] }).outcomes?.[0] ?? null)
    : null

  return (
    <aside className="rail" aria-label="Status and actions">
      <div className={`panel lead${next.tone === 'warn' ? ' warn' : ''}`}>
        <h2>Next step</h2>
        <div className="panel-body">
          <strong>{next.headline}</strong>
          <p className="hint tight-above">{next.detail}</p>
          {next.action && (
            <button
              className="primary block"
              onClick={() => {
                if (next.action!.cycleId != null) onOpenCycle(next.action!.cycleId)
                else if (next.action!.jump) onJump(next.action!.jump)
              }}
            >
              {next.action.label} →
            </button>
          )}
        </div>
      </div>

      <div className="panel">
        <h2>Status</h2>
        <div className="panel-body">
          <div className="stat-row">
            <div>
              <div className="stat" style={{ color: (c.open_awaiting_review ?? 0) > 0 ? 'var(--warn)' : undefined }}>
                {(c.open_awaiting_review ?? 0).toLocaleString()}
              </div>
              <div className="stat-label">waiting</div>
            </div>
            <div><div className="stat">{(c.open_approved ?? 0).toLocaleString()}</div><div className="stat-label">approved</div></div>
            <div><div className="stat">{(c.sent_total ?? 0).toLocaleString()}</div><div className="stat-label">sent</div></div>
          </div>
          <dl className="facts">
            <dt>Loop</dt>
            <dd>{c.enabled ? `On · next ${when(c.next_run_at)}` : 'Off'}</dd>
            <dt>Last look</dt>
            <dd>{c.last_run_at ? new Date(c.last_run_at).toLocaleString() : 'never'}</dd>
            <dt>Copy</dt>
            <dd>
              {c.copy_approved
                ? <>approved by {c.copy_approved_by}<div className="dim">{new Date(c.copy_approved_at!).toLocaleString()}</div></>
                : <span style={{ color: 'var(--warn)' }}>not approved</span>}
            </dd>
          </dl>
        </div>
      </div>

      <div className="panel">
        <h2>Actions</h2>
        <div className="panel-body">
          <div>
            <label htmlFor="who">Your name <span className="label-note">— recorded on approve and arm</span></label>
            <input id="who" type="text" value={who} onChange={(e) => setWho(e.target.value)} placeholder="your name" />
          </div>
          {!c.copy_approved && (
            <div>
              <button
                className="primary block"
                disabled={busy !== null || noName}
                title={noName ? 'enter your name first' : undefined}
                onClick={() => void act('approve', () => api.approveCopy(c.id, who.trim()), 'copy and prompt approved')}
              >
                Approve copy &amp; prompt
              </button>
              <p className="hint caption">Confirms you read the copy and AI instructions on Setup.</p>
            </div>
          )}
          <div>
            {c.enabled ? (
              <button className="block" disabled={busy !== null} onClick={() => void act('disable', () => api.disableCampaign(c.id), 'campaign switched off')}>
                Turn off
              </button>
            ) : (
              <button
                className="block"
                disabled={busy !== null || enableBlockers.length > 0}
                title={enableBlockers.join('; ') || undefined}
                onClick={() => void act('enable', () => api.enableCampaign(c.id), 'campaign switched on — the scheduler will run it')}
              >
                Turn on
              </button>
            )}
            <p className="hint caption">
              {c.enabled
                ? `Runs every ${c.interval_hours}h on its own.`
                : enableBlockers.length
                  ? `Cannot turn on: ${enableBlockers.join('; ')}.`
                  : 'Runs it on its schedule.'}
            </p>
          </div>
          <div>
            <button
              className="block"
              disabled={busy !== null || !c.copy_approved}
              title={!c.copy_approved ? 'copy must be approved first' : undefined}
              onClick={() => void runNow()}
            >
              {busy === 'run' ? <span className="row" style={{ gap: 6, justifyContent: 'center' }}><span className="spinner" /> Running…</span> : 'Run one cycle now'}
            </button>
            <p className="hint caption">Drafts for review only — never sends. Works while off.</p>
          </div>
          {job && (
            <div className={job.status === 'failed' ? 'error' : 'notice'} style={{ fontSize: 12.5 }}>
              <span className={`badge ${job.status === 'running' ? 'running' : job.status === 'completed' ? 'ok' : 'bad'}`}>{job.status}</span>{' '}
              {outcome ? (
                <>
                  {outcome.matched} matched, {outcome.drafted} drafted
                  {Object.keys(outcome.skipped ?? {}).length > 0 && (
                    <>, skipped {Object.entries(outcome.skipped).map(([k, n]) => `${n} ${k.replace('_', ' ')}`).join(', ')}</>
                  )}
                  {outcome.error && <> — {outcome.error}</>}
                </>
              ) : job.status === 'running' ? 'scanning the CRM…' : String((job.detail as { error?: string }).error ?? '')}
            </div>
          )}
        </div>
      </div>

      <div className="panel">
        <h2>Sending</h2>
        <div className="panel-body">
          <div>
            {c.agent_release_allowed ? (
              <>
                <span className="badge parked" style={{ whiteSpace: 'normal' }}>
                  open to agent release · {c.agent_release_allowed_by} · {new Date(c.agent_release_allowed_at!).toLocaleString()}
                </span>
                <button className="block" style={{ marginTop: 'var(--s2)' }} disabled={busy !== null} onClick={() => void act('agent-release', () => api.setAgentRelease(c.id, false, who.trim() || 'operator'), 'closed to agent release')}>
                  Close to agent release
                </button>
              </>
            ) : (
              <button
                className="block"
                disabled={busy !== null || !c.copy_approved || noName}
                title={!c.copy_approved ? 'approve the copy first' : noName ? 'enter your name first' : undefined}
                onClick={() => void act('agent-release', () => api.setAgentRelease(c.id, true, who.trim()), 'open to agent release — an agent may now send a batch you authorize on a cycle')}
              >
                Open to agent release
              </button>
            )}
            <p className="hint caption">An agent may send only a batch you authorize on a cycle — never approve or arm.</p>
          </div>

          {c.message_class === 'service' && (
            <div>
              {c.cadence_waived ? (
                <>
                  <span className="badge parked" style={{ whiteSpace: 'normal' }}>
                    send gap waived · {c.cadence_waived_by} · {new Date(c.cadence_waived_at!).toLocaleString()}
                  </span>
                  <button className="block" style={{ marginTop: 'var(--s2)' }} disabled={busy !== null} onClick={() => void act('cadence-waiver', () => api.setCadenceWaiver(c.id, false, who.trim() || 'operator'), 'send gap restored')}>
                    Restore the send gap
                  </button>
                </>
              ) : (
                <button
                  className="block"
                  disabled={busy !== null || !c.copy_approved || noName}
                  title={!c.copy_approved ? 'approve the copy first' : noName ? 'enter your name first' : undefined}
                  onClick={() => void act('cadence-waiver', () => api.setCadenceWaiver(c.id, true, who.trim()), 'send gap waived — this billing notice may go to people mailed in the last 14 days')}
                >
                  Waive the send gap
                </button>
              )}
              <p className="hint caption">Lets this billing notice reach people mailed in the last 14 days. The yearly cap and bounce blocks still apply.</p>
            </div>
          )}

          {c.dispatch_mode === 'manual' ? (
            <p className="hint">Sending is manual — a person sends each cycle from its Send step. Nothing to arm.</p>
          ) : c.armed ? (
            <div className="notice warn">
              <strong>Armed</strong> by {c.auto_dispatch_armed_by} at {new Date(c.auto_dispatch_armed_at!).toLocaleString()}. The scheduler sends approved drafts with nobody present.
              <button className="block" style={{ marginTop: 'var(--s2)' }} disabled={busy !== null} onClick={() => void act('disarm', () => api.disarmCampaign(c.id, who.trim() || 'operator'), 'disarmed')}>
                Disarm
              </button>
            </div>
          ) : (
            <div className="notice warn">
              <strong>Automatic sending is not armed.</strong> The scheduler fills the review queue and sends nothing. Type <span className="mono">{c.slug}</span> to arm.
              {armBlockers.length > 0 && <div className="hint caption">Cannot arm yet: {armBlockers.join('; ')}.</div>}
              <input type="text" style={{ marginTop: 'var(--s2)' }} value={typedSlug} onChange={(e) => setTypedSlug(e.target.value)} placeholder="type the slug" spellCheck={false} aria-label="Type the slug to arm" />
              <button
                className="danger block"
                style={{ marginTop: 'var(--s2)' }}
                disabled={busy !== null || armBlockers.length > 0 || typedSlug.trim() !== c.slug || noName}
                onClick={() => void act('arm', () => api.armCampaign(c.id, typedSlug.trim(), who.trim()), 'ARMED — the scheduler may now send this campaign')}
              >
                Arm automatic sending
              </button>
            </div>
          )}
        </div>
      </div>

      {tab === 'setup' && (
        <div className="panel">
          <h2>Jump to</h2>
          <div className="panel-body">
            <div className="rail-list">
              {SETUP_SECTIONS.map((s) => (
                <button key={s.id} className="rail-item" onClick={() => onJump(s.id)}>{s.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}

/**
 * Save and Revert for one section, with the consequence named BEFORE the
 * click. Dropdowns used to save the moment they changed, and five of these
 * fields withdraw copy approval — so one slip of a select took the campaign
 * back to unapproved with only an after-the-fact notice to say so.
 */
function SaveBar({
  changed,
  withdraws,
  label,
  onSave,
  onRevert,
}: {
  changed: boolean
  withdraws: boolean
  label: string
  onSave: () => void
  onRevert: () => void
}) {
  return (
    <div className="row">
      <button className="primary" disabled={!changed} onClick={onSave}>{label}</button>
      {changed && <button onClick={onRevert}>Revert</button>}
      {changed && withdraws && (
        <span className="hint" style={{ color: 'var(--warn)' }}>Saving withdraws copy approval — a person must approve again.</span>
      )}
    </div>
  )
}

type SaveFn = (b: Record<string, unknown>, w: string) => Promise<void>

function AudiencePanel({ c, onSave }: { c: Campaign; onSave: SaveFn }) {
  const [filters, setFilters] = useState<Filter[] | null>(null)
  const [filter, setFilter] = useState(c.filter)
  useEffect(() => {
    void api.filters().then(setFilters).catch(() => setFilters([]))
  }, [])
  useEffect(() => setFilter(c.filter), [c.updated_at])
  return (
    <div className="panel" id="sec-audience">
      <h2>Audience</h2>
      <div className="panel-body">
        <div style={{ maxWidth: 520 }}>
          <label htmlFor="aud-filter">Filter</label>
          <select id="aud-filter" value={filter} disabled={!filters} style={{ width: '100%' }} onChange={(e) => setFilter(e.target.value)}>
            {(filters ?? [{ name: c.filter, title: c.filter_title } as Filter]).map((f) => (
              <option key={f.name} value={f.name} disabled={f.parked || f.unconditional}>
                {f.title} — {f.name}{f.parked ? ' (parked)' : ''}{f.unconditional ? ' (no condition)' : ''}
              </option>
            ))}
          </select>
        </div>
        <dl className="facts">
          <dt>Condition</dt>
          <dd className="mono">{c.filter_when ?? <span className="badge bad">none — matches everyone</span>}</dd>
          <dt>Copy group</dt>
          <dd className="mono">{c.legacy_group ?? <span className="dim">none — library fallbacks only</span>}</dd>
        </dl>
        <p className="hint">Filters are audiences, not an assignment — an account may match several campaigns.</p>
        <SaveBar
          changed={filter !== c.filter}
          withdraws
          label="Save audience"
          onSave={() => void onSave({ filter }, 'Audience')}
          onRevert={() => setFilter(c.filter)}
        />
      </div>
    </div>
  )
}

function CopyPanel({ c, onSave }: { c: Campaign; onSave: SaveFn }) {
  const [draft, setDraft] = useState<Record<string, string>>(() => slotsOf(c))
  useEffect(() => setDraft(slotsOf(c)), [c.updated_at])
  const changed = COPY_SLOTS.some((s) => (draft[s] ?? '') !== (c.copy[s] ?? ''))
  return (
    <div className="panel" id="sec-copy">
      <h2>
        Copy <span className="count">{COPY_SLOTS.filter((s) => (c.copy[s] ?? '').trim()).length} of 6 slots set</span>
      </h2>
      <div className="panel-body">
        <p className="hint">
          Six slots for every draft. Empty slots fall through to the library for this filter&apos;s
          copy group. <span className="mono">{'{{merge_fields}}'}</span> allowed.
        </p>
        {COPY_SLOTS.map((slot) => (
          <div className="field" key={slot}>
            <label htmlFor={`copy-${slot}`}>
              {slot}{' '}
              {!(c.copy[slot] ?? '').trim() && <span className="label-note">— from the library</span>}
            </label>
            <textarea
              id={`copy-${slot}`}
              rows={slot === 'subject' || slot === 'cta' ? 1 : 3}
              value={draft[slot] ?? ''}
              spellCheck
              onChange={(e) => setDraft({ ...draft, [slot]: e.target.value })}
            />
          </div>
        ))}
        <SaveBar
          changed={changed}
          withdraws
          label="Save copy"
          onSave={() => void onSave({ copy: clean(draft) }, 'Copy')}
          onRevert={() => setDraft(slotsOf(c))}
        />
      </div>
    </div>
  )
}

function slotsOf(c: Campaign): Record<string, string> {
  const out: Record<string, string> = {}
  for (const s of COPY_SLOTS) out[s] = c.copy[s as CopySlot] ?? ''
  return out
}

function clean(d: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(d)) if (v.trim()) out[k] = v.trim()
  return out
}

function PromptPanel({ c, onSave }: { c: Campaign; onSave: SaveFn }) {
  const [prompt, setPrompt] = useState(c.system_prompt ?? '')
  const [open, setOpen] = useState(false)
  useEffect(() => setPrompt(c.system_prompt ?? ''), [c.updated_at])
  return (
    <div className="panel" id="sec-prompt">
      <h2>
        AI instructions <span className="count">{prompt.length.toLocaleString()} characters</span>
      </h2>
      <div className="panel-body">
        <div className="row">
          <button onClick={() => setOpen(!open)}>{open ? '▾ Hide' : '▸ Show and edit'}</button>
          <span className="hint">How the model rewrites this campaign&apos;s drafts.</span>
        </div>
        {open && (
          <>
            <textarea rows={16} value={prompt} onChange={(e) => setPrompt(e.target.value)} spellCheck={false} />
            <SaveBar
              changed={prompt !== (c.system_prompt ?? '')}
              withdraws
              label="Save instructions"
              onSave={() => void onSave({ system_prompt: prompt }, 'AI instructions')}
              onRevert={() => setPrompt(c.system_prompt ?? '')}
            />
          </>
        )}
      </div>
    </div>
  )
}

function StylePanel({ c, onSave }: { c: Campaign; onSave: SaveFn }) {
  const [styles, setStyles] = useState<StyleInfo[] | null>(null)
  const [style, setStyle] = useState(c.style ?? '')
  const [cls, setCls] = useState(c.message_class)
  useEffect(() => {
    void api.styles().then((r) => setStyles(r.styles)).catch(() => setStyles([]))
  }, [])
  useEffect(() => {
    setStyle(c.style ?? '')
    setCls(c.message_class)
  }, [c.updated_at])
  const body: Record<string, unknown> = {}
  if (style !== (c.style ?? '')) body.style = style
  if (cls !== c.message_class) body.message_class = cls
  return (
    <div className="panel" id="sec-look">
      <h2>Look</h2>
      <div className="panel-body">
        <p className="hint">The email&apos;s frame only — the words are the copy above.</p>
        <div className="row-form">
          <div style={{ minWidth: 300 }}>
            <label htmlFor="look-style">Style</label>
            <select id="look-style" value={style} disabled={!styles} style={{ width: '100%' }} onChange={(e) => setStyle(e.target.value)}>
              {(styles ?? []).map((s) => (
                <option key={s.name} value={s.name} disabled={s.problems.length > 0}>
                  {s.name} — {s.title}{s.problems.length ? ' (unusable)' : ''}
                </option>
              ))}
              {!styles?.some((s) => s.name === c.style) && c.style && <option value={c.style}>{c.style}</option>}
            </select>
          </div>
          <div style={{ minWidth: 260 }}>
            <label htmlFor="look-class">Kind of email</label>
            <select id="look-class" value={cls} style={{ width: '100%' }} onChange={(e) => setCls(e.target.value as Campaign['message_class'])}>
              <option value="campaign">campaign — with unsubscribe footer</option>
              <option value="service">service — about their own account, no footer</option>
            </select>
          </div>
        </div>
        {styles?.find((s) => s.name === style)?.against && (
          <p className="hint"><strong>Not for:</strong> {styles.find((s) => s.name === style)!.against}</p>
        )}
        <SaveBar
          changed={Object.keys(body).length > 0}
          withdraws
          label="Save look"
          onSave={() => void onSave(body, 'Look')}
          onRevert={() => { setStyle(c.style ?? ''); setCls(c.message_class) }}
        />
      </div>
    </div>
  )
}

function SchedulePanel({ c, onSave }: { c: Campaign; onSave: SaveFn }) {
  const [review, setReview] = useState(c.review_mode)
  const [dispatch, setDispatch] = useState(c.dispatch_mode)
  useEffect(() => {
    setReview(c.review_mode)
    setDispatch(c.dispatch_mode)
  }, [c.updated_at])
  const body: Record<string, unknown> = {}
  if (review !== c.review_mode) body.review_mode = review
  if (dispatch !== c.dispatch_mode) body.dispatch_mode = dispatch
  return (
    <div className="panel" id="sec-schedule">
      <h2>Schedule &amp; policy</h2>
      <div className="panel-body">
        <dl className="facts">
          <dt>Looks every</dt>
          <dd>
            <NumberField value={c.interval_hours} min={1} suffix="hours" onSave={(v) => void onSave({ interval_hours: v }, 'Interval')} />
            <div className="hint caption">
              When we look. Next: {c.enabled ? when(c.next_run_at) : 'off'}; last:{' '}
              {c.last_run_at ? new Date(c.last_run_at).toLocaleString() : 'never'}.
            </div>
          </dd>
          <dt>Cooldown</dt>
          <dd>
            <NumberField value={c.cooldown_days} min={0} nullable suffix="days" onSave={(v) => void onSave({ cooldown_days: v }, 'Cooldown')} />
            <div className="hint caption">
              After a send, how long before this campaign may write to the same person again. Empty
              = cadence default. Account caps still apply across campaigns.
            </div>
          </dd>
          <dt>Lifetime cap</dt>
          <dd>
            <NumberField value={c.max_sends} min={1} nullable suffix="sends, ever" onSave={(v) => void onSave({ max_sends: v }, 'Lifetime cap')} />
          </dd>
          <dt>Review</dt>
          <dd>
            <select value={review} aria-label="Review" onChange={(e) => setReview(e.target.value as Campaign['review_mode'])}>
              <option value="human">human — drafts wait for a person (or a flagged agent)</option>
              <option value="auto">auto — the scheduler approves drafts that pass the checks</option>
            </select>
          </dd>
          <dt>Sending</dt>
          <dd>
            <select value={dispatch} aria-label="Sending" onChange={(e) => setDispatch(e.target.value as Campaign['dispatch_mode'])}>
              <option value="manual">manual — a person types the token on each cycle</option>
              <option value="auto">auto — the scheduler sends, once a person has ARMED this campaign</option>
            </select>
          </dd>
        </dl>
        <SaveBar
          changed={Object.keys(body).length > 0}
          withdraws={false}
          label="Save policy"
          onSave={() => void onSave(body, 'Policy')}
          onRevert={() => { setReview(c.review_mode); setDispatch(c.dispatch_mode) }}
        />
      </div>
    </div>
  )
}

function CyclesPanel({ c, onOpenCycle, onChanged }: { c: Campaign; onOpenCycle: (id: number) => void; onChanged: () => Promise<void> }) {
  const cycles: Cycle[] = c.cycles ?? []
  const [busy, setBusy] = useState<number | null>(null)
  const cancel = async (id: number) => {
    setBusy(id)
    try { await api.cancelCycle(id); await onChanged() } finally { setBusy(null) }
  }
  return (
    <div className="panel">
      <h2>Cycles <span className="count">{cycles.length} most recent</span></h2>
      {cycles.length === 0 ? (
        <div className="empty">No cycles yet. Run one now, or turn the campaign on.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="num">#</th>
              <th>Started</th>
              <th>Status</th>
              <th className="num">People</th>
              <th className="num">Skipped</th>
              <th className="num">Drafts</th>
              <th className="num">Waiting</th>
              <th className="num">Approved</th>
              <th className="num">Sent</th>
              <th className="num">Holding</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {cycles.map((cy) => (
              <tr key={cy.id} className="clickable" onClick={() => onOpenCycle(cy.id)}>
                <td className="num mono">{cy.id}</td>
                <td>{new Date(cy.started_at).toLocaleString()} <span className="dim">· {cy.triggered_by}</span></td>
                <td>
                  <CycleBadge status={cy.status} /> <StaleBadge kind={cy.stale_kind} />
                </td>
                <td className="num">{cy.members}</td>
                <td className="num">{cy.skipped}</td>
                <td className="num">{cy.drafts}</td>
                <td className="num">{cy.awaiting_review}</td>
                <td className="num">{cy.approved}</td>
                <td className="num">{cy.sent}</td>
                <td className="num" title={cy.open ? 'accounts no later tick of this campaign will draft until this cycle closes' : 'closed — it holds nobody'}>
                  {cy.holding > 0 ? cy.holding : <span className="dim">—</span>}
                </td>
                <td>
                  <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                    <button onClick={(e) => { e.stopPropagation(); onOpenCycle(cy.id) }}>Open</button>
                    {!['sent', 'cancelled', 'failed'].includes(cy.status) && (
                      <button disabled={busy === cy.id} onClick={(e) => { e.stopPropagation(); void cancel(cy.id) }}>Cancel</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function ExclusionsPanel({ c, onChanged }: { c: Campaign; onChanged: () => Promise<void> }) {
  const [id, setId] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const add = async () => {
    setError(null)
    try {
      await api.addExclusion(c.id, Number(id), reason.trim() || undefined)
      setId(''); setReason('')
      await onChanged()
    } catch (err) {
      setError(String((err as Error).message))
    }
  }
  return (
    <div className="panel">
      <h2>Never mail <span className="count">{c.exclusions?.length ?? 0}</span></h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        <p className="hint">Accounts this campaign skips whatever its filter says. Recorded on every cycle as <span className="mono">excluded</span>.</p>
        <div className="row-form">
          <div style={{ width: 140 }}>
            <label>Account id</label>
            <input type="number" value={id} onChange={(e) => setId(e.target.value)} />
          </div>
          <div style={{ minWidth: 220 }}>
            <label>Reason</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <button disabled={!id} onClick={() => void add()}>Add</button>
        </div>
        {(c.exclusions ?? []).length > 0 && (
          <table className="tight" style={{ marginTop: 'var(--s2)' }}>
            <tbody>
              {c.exclusions!.map((x) => (
                <tr key={x.account_id}>
                  <td className="mono">{x.account_id}</td>
                  <td className="dim">{x.reason ?? '—'}</td>
                  <td className="dim">{x.added_by}</td>
                  <td><button onClick={() => void api.removeExclusion(c.id, x.account_id).then(onChanged)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function DangerPanel({ c, onDeleted }: { c: Campaign; onDeleted: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState('')
  return (
    <div className="panel">
      <h2>Delete</h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        <p className="hint">
          Refused once this campaign has sent anything — cadence reads that history for ever.
          Disable it instead. Type the slug to delete a campaign that never sent.
        </p>
        <div className="row-form">
          <input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={c.slug} spellCheck={false} />
          <button
            className="danger"
            disabled={confirm !== c.slug}
            onClick={() => void api.deleteCampaign(c.id).then(onDeleted).catch((e) => setError(String((e as Error).message)))}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

function NumberField({
  value,
  min,
  nullable = false,
  suffix,
  onSave,
}: {
  value: number | null
  min: number
  nullable?: boolean
  suffix?: string
  onSave: (v: number | null) => void
}) {
  const [text, setText] = useState(value == null ? '' : String(value))
  useEffect(() => setText(value == null ? '' : String(value)), [value])
  const parsed = text.trim() === '' ? null : Number(text)
  const valid = parsed === null ? nullable : Number.isInteger(parsed) && parsed >= min
  const changed = parsed !== value
  return (
    <span className="row" style={{ gap: 6 }}>
      <input type="number" min={min} value={text} style={{ width: 90 }} onChange={(e) => setText(e.target.value)} />
      {suffix && <span className="hint">{suffix}</span>}
      {changed && <button disabled={!valid} onClick={() => onSave(parsed)}>Save</button>}
    </span>
  )
}
